import { computed, ref } from 'vue'

function matchesQuery(label, query) {
  return label.toLowerCase().includes(query.toLowerCase())
}

function filterItems(items, query) {
  if (!query)
    return items

  return items.reduce((accumulator, item) => {
    if (item.separator)
      return accumulator

    const labelMatch = matchesQuery(item.label, query)

    if (item.hasChildren && item.children?.length) {
      const filteredChildren = filterItems(item.children, query)
      if (labelMatch || filteredChildren.length > 0) {
        accumulator.push({
          ...item,
          children: labelMatch ? item.children : filteredChildren,
        })
      }
      return accumulator
    }

    if (labelMatch)
      accumulator.push(item)

    return accumulator
  }, [])
}

function flattenItems(items) {
  return items.reduce((accumulator, item) => {
    if (item.separator)
      return accumulator
    accumulator.push(item)
    if (item.hasChildren && item.children?.length)
      accumulator.push(...flattenItems(item.children))
    return accumulator
  }, [])
}

export function useMenuSearch() {
  const searchQuery = ref('')
  const focusedIndex = ref(-1)

  function clearSearch() {
    searchQuery.value = ''
    focusedIndex.value = -1
  }

  function applySearch(items) {
    return computed(() => filterItems(items.value, searchQuery.value))
  }

  const hasQuery = computed(() => searchQuery.value.trim().length > 0)

  function buildKeyboardHandler(visibleItems, router) {
    return function onSearchKeydown(event) {
      const flat = flattenItems(visibleItems.value)
      if (!flat.length)
        return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        focusedIndex.value = focusedIndex.value < flat.length - 1
          ? focusedIndex.value + 1
          : 0
      }
      else if (event.key === 'ArrowUp') {
        event.preventDefault()
        focusedIndex.value = focusedIndex.value > 0
          ? focusedIndex.value - 1
          : flat.length - 1
      }
      else if (event.key === 'Enter' && focusedIndex.value >= 0) {
        event.preventDefault()
        const item = flat[focusedIndex.value]
        if (item?.route)
          router.push(item.route).catch(() => {})
      }
    }
  }

  function isFocused(item, visibleItems) {
    if (focusedIndex.value < 0)
      return false
    const flat = flattenItems(visibleItems.value)
    return flat[focusedIndex.value]?.key === item.key
  }

  return {
    searchQuery,
    hasQuery,
    focusedIndex,
    clearSearch,
    applySearch,
    buildKeyboardHandler,
    isFocused,
  }
}
